import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useLocation, useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand, faVideo } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { ParticipantAudioTracks } from "../ParticipantAudioTracks/ParticipantAudioTracks";
import ParticipantTracks from "../ParticipantTracks/ParticipantTracks";
import { useVideoState } from "../../../state";
import useRoomState from "../../../hooks/useRoomState/useRoomState";
import useParticipants from "../../../hooks/useParticipants/useParticipants";
import { typography } from "../../../utils/typography";
import { semanticColors } from "../../../utils/colors";

const CARD_WIDTH = 288;
// approx: header ~40px + video 162px + footer ~50px
const CARD_HEIGHT = 252;

function getInitialPos() {
  return {
    x: window.innerWidth - 24 - CARD_WIDTH,
    y: window.innerHeight - 96 - CARD_HEIGHT
  };
}

function PiPVideo() {
  const { t } = useTranslation();
  const participants = useParticipants();
  const first = participants[0];

  if (!first) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Typography
          sx={{ ...typography.body.s, color: "rgba(255,255,255,0.5)" }}
        >
          {t("floatingVideo.waiting")}
        </Typography>
      </Box>
    );
  }

  return <ParticipantTracks participant={first} videoOnly />;
}

export default function FloatingVideoWindow() {
  const { t } = useTranslation();
  const roomState = useRoomState();
  const { isPiPMode, setIsPiPMode, pipPatientName } = useVideoState();
  const location = useLocation();
  const history = useHistory();

  const [pos, setPos] = useState(getInitialPos);
  const [isDragging, setIsDragging] = useState(false);
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Reset to bottom-right whenever PiP mode is activated
  useEffect(() => {
    if (isPiPMode) {
      setPos(getInitialPos());
    }
  }, [isPiPMode]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      setPos({
        x: Math.max(
          0,
          Math.min(
            e.clientX - dragOffset.current.x,
            window.innerWidth - CARD_WIDTH
          )
        ),
        y: Math.max(
          0,
          Math.min(
            e.clientY - dragOffset.current.y,
            window.innerHeight - CARD_HEIGHT
          )
        )
      });
    };

    const onMouseUp = () => {
      dragging.current = false;
      setIsDragging(false);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const handleDragStart = (e: React.MouseEvent) => {
    dragging.current = true;
    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y
    };
  };

  const isConnected = roomState === "connected" || roomState === "reconnecting";
  const isOnVideoPage =
    location.pathname.startsWith("/video") ||
    location.pathname.startsWith("/invite-video");

  const handleReturnFullscreen = () => {
    setIsPiPMode(false);
    history.push("/video");
  };

  if (!isConnected) return null;

  return (
    <>
      {!isOnVideoPage && <ParticipantAudioTracks />}

      {isPiPMode && !isOnVideoPage && (
        <Box
          sx={{
            position: "fixed",
            top: pos.y,
            left: pos.x,
            zIndex: 1300,
            width: CARD_WIDTH,
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
            userSelect: "none"
          }}
        >
          <Box
            onMouseDown={handleDragStart}
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "rgba(0,0,0,0.88)",
              cursor: isDragging ? "grabbing" : "grab"
            }}
          >
            <FontAwesomeIcon
              icon={faVideo}
              style={{ color: "#0DBDA7", fontSize: 12, flexShrink: 0 }}
            />
            <Typography
              sx={{
                ...typography.body.s,
                color: "#fff",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {pipPatientName || t("floatingVideo.title")}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              height: 162,
              position: "relative",
              bgcolor: "#0d0d1a"
            }}
          >
            <PiPVideo />
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: "rgba(0,0,0,0.88)",
              display: "flex",
              justifyContent: "center"
            }}
          >
            <Box
              component="button"
              onClick={handleReturnFullscreen}
              sx={{
                background: semanticColors.gradient.surface,
                borderRadius: "40px",
                height: "34px",
                border: "none",
                cursor: "pointer",
                px: "16px",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              <FontAwesomeIcon
                icon={faExpand}
                style={{ color: "#fff", fontSize: 11 }}
              />
              <Typography sx={{ ...typography.body.s, color: "#fff" }}>
                {t("floatingVideo.returnToConsultation")}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
